import type { AssetCategory, AssetDefinition } from './types'
import { CATEGORY_ORDER } from './types'

/**
 * Auto-registering catalog: any `src/assets/items/*.tsx` that exports
 * `definition: AssetDefinition` is picked up at build time. Adding furniture
 * never requires touching a barrel file.
 */
const modules = import.meta.glob<{ definition?: AssetDefinition }>('./items/*.tsx', {
  eager: true,
})

function loadAssets(): AssetDefinition[] {
  const found: AssetDefinition[] = []
  const seen = new Set<string>()

  for (const path of Object.keys(modules).sort()) {
    const definition = modules[path]?.definition
    if (!definition) {
      console.warn(`[catalog] ${path} does not export a "definition"`)
      continue
    }
    if (seen.has(definition.id)) {
      console.warn(`[catalog] duplicate asset id "${definition.id}" in ${path}`)
      continue
    }
    seen.add(definition.id)
    found.push(definition)
  }

  return found
}

export const ASSETS: AssetDefinition[] = loadAssets()

export const ASSETS_BY_ID: ReadonlyMap<string, AssetDefinition> = new Map(
  ASSETS.map((asset) => [asset.id, asset]),
)

export function getAsset(id: string): AssetDefinition | undefined {
  return ASSETS_BY_ID.get(id)
}

export interface CatalogGroup {
  category: AssetCategory
  assets: AssetDefinition[]
}

export function groupedAssets(filter = ''): CatalogGroup[] {
  const needle = filter.trim().toLowerCase()
  const matches = (asset: AssetDefinition) => {
    if (!needle) return true
    const haystack = [asset.name, asset.id, ...(asset.keywords ?? [])].join(' ').toLowerCase()
    return needle.split(/\s+/).every((token) => haystack.includes(token))
  }

  return CATEGORY_ORDER.map((category) => ({
    category,
    assets: ASSETS.filter((asset) => asset.category === category && matches(asset)),
  })).filter((group) => group.assets.length > 0)
}
